import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getZoneList } from "../../../redux/action/zone_action";
import Select from "react-select";

const ZoneDropDown = ({
  setFilterValue,
  defaultToLocalStorage = true,
  value,
  includeAllOption = false,
  defaultSelectAll = false,
  onAllZoneIdsChange,
  allValue = "All",
}) => {
  const dispatch = useDispatch();
  const { zone } = useSelector((state) => state.zoneReducer);

  const [selectedOption, setSelectedOption] = useState(null);
  const lastSyncedAllZoneKeyRef = useRef("");

  useEffect(() => {
    dispatch(getZoneList());
  }, [dispatch]);

  const key = "zone_id";
  const options = useMemo(() => {
    const modifiedData = zone?.map((ele) => ({
      ...ele,
      zone_id: ele.zone_id,
    }));

    const mappedOptions =
      modifiedData?.map((zoneItem) => ({
        value: zoneItem.zone_id,
        label: zoneItem?.zone_name
          ? zoneItem?.zone_name
          : zoneItem?.zone_obj?.zone_name,
      })) || [];

    if (includeAllOption && mappedOptions.length) {
      return [{ value: allValue, label: "All" }, ...mappedOptions];
    }

    return mappedOptions;
  }, [allValue, includeAllOption, zone]);

  const allottedZoneIds = useMemo(() => {
    return options
      .filter((option) => option.value !== allValue)
      .map((option) => option.value);
  }, [allValue, options]);

  useEffect(() => {
    const normalizeValue = (selectedValue) => {
      if (
        selectedValue === "" ||
        selectedValue === null ||
        selectedValue === undefined
      ) {
        return null;
      }

      if (selectedValue === allValue) {
        return allValue;
      }

      const numericValue = Number(selectedValue);
      return Number.isNaN(numericValue) ? selectedValue : numericValue;
    };

    if (value !== undefined) {
      const normalizedValue = normalizeValue(value);
      const effectiveValue =
        normalizedValue === null && defaultSelectAll && includeAllOption
          ? allValue
          : normalizedValue;
      const selectedByFilter =
        options.find((option) => option.value === effectiveValue) || null;
      setSelectedOption((prev) => {
        const prevValue = prev?.value ?? null;
        const nextValue = selectedByFilter?.value ?? null;
        return prevValue === nextValue ? prev : selectedByFilter;
      });
      return;
    }

    if (defaultToLocalStorage) {
      const zoneId = Number(localStorage.getItem("zone_id"));
      const defaultValue = options.find((option) => option.value === zoneId);
      setSelectedOption((prev) => {
        const prevValue = prev?.value ?? null;
        const nextValue = defaultValue?.value ?? null;
        return prevValue === nextValue ? prev : defaultValue || null;
      });
      return;
    }

    if (defaultSelectAll && includeAllOption) {
      const allOption = options.find((option) => option.value === allValue);
      setSelectedOption((prev) => {
        const prevValue = prev?.value ?? null;
        const nextValue = allOption?.value ?? null;
        return prevValue === nextValue ? prev : allOption || null;
      });
      return;
    }

    setSelectedOption((prev) => (prev === null ? prev : null));
  }, [allValue, defaultSelectAll, includeAllOption, options, defaultToLocalStorage, value]);

  useEffect(() => {
    if (typeof onAllZoneIdsChange !== "function") {
      return;
    }

    const syncKey =
      selectedOption?.value === allValue
        ? `all:${allottedZoneIds.join(",")}`
        : "single:";

    if (lastSyncedAllZoneKeyRef.current === syncKey) {
      return;
    }

    lastSyncedAllZoneKeyRef.current = syncKey;

    if (selectedOption?.value === allValue) {
      onAllZoneIdsChange(allottedZoneIds);
      return;
    }

    onAllZoneIdsChange([]);
  }, [allValue, allottedZoneIds, onAllZoneIdsChange, selectedOption]);

  return (
    <Select
      options={options}
      value={selectedOption}
      isClearable
      placeholder="Select Zone"
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      onChange={(selectedOption) => {
        let nextSelectedOption = selectedOption;

        if (!nextSelectedOption && defaultSelectAll && includeAllOption) {
          nextSelectedOption =
            options.find((option) => option.value === allValue) || null;
        }

        setSelectedOption(nextSelectedOption);
        const selectedValue = nextSelectedOption ? nextSelectedOption.value : "";
        setFilterValue(key, selectedValue);
      }}
      className="react-select-container is-invalid"
    />
  );
};

export default ZoneDropDown;
